import { Ellipsis } from 'lucide-react';
import close from 'images/icons/orders/close.svg';
import system_setting_icon from 'images/setting_icons/system_setting_icon.svg';
import * as React from 'react';
import { useRecoilValue } from 'recoil';
import { AUsers } from '../../atoms/global/users';
import Add from '../../components_v2/add/Add';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import Input from '../../components_v2/input/Input';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { DeletionContainer, DeletionHeader } from '../../components_v2/popup/PopupDeletion';
import { Column, Table } from '../../components_v2/table/Table';
import RefreshButton from '../../myToolbar/RefreshButton';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { DarkGreySidely, GreySidely3 } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { useEditFormsPermission } from '../../utils/customHooks';
import { AlertRes } from '../alert/AlertProvider';
import useAlert from '../alert/UseAlert';
import { ToolbarImageContainer } from '../globals/defaultToolbar/style/Style';
import { ComponentProps } from '../globals/mainPage/mainPage';
import { LoadingStateEnum } from '../import/model';
import { ComponentLoader } from '../map/modalRight/ModalCalendar';
import { TableRow } from '../orders/templateOrders/style/Style';
import { FlexDiv } from '../products/style';
import { deleteTypedMetadata, generateMetadata, getTypedMetadata, MetadataType, postTypedMetadata, putTypedMetadata, TypedMetadata } from './actions';
import { TOOLBARS_HEIGHT } from '../globals/defaultToolbar/style/Style';

export default function MetadataCreator(props: ComponentProps) {
	const [metadata, setMetadata] = React.useState<TypedMetadata[]>([]);
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [loadingRefresh, setLoadingRefresh] = React.useState<boolean>(false);
	const [editPopupOpen, setEditPopupOpen] = React.useState<boolean>(false);
	const users = useRecoilValue(AUsers);
	const [newMetadata, setNewMetadata] = React.useState<Partial<TypedMetadata>>({ key: '', type: 'string' });
	const isAllowedToEditForms = useEditFormsPermission();
	const [isAllowedToClickGenerate, setIsAllowedToClickGenerate] = React.useState<boolean>(true);
	const [diffMinutes, setDiffMinutes] = React.useState<number>(0);
	const enableSave = newMetadata.key && newMetadata.type && newMetadata.key.trim().length > 0 && newMetadata.type.length > 0;
	const alert = useAlert();

	const dataList: { value: MetadataType; label: string }[] = [
		{ value: 'campaign', label: translateToString('metadata.types.campaign') },
		{ value: 'product', label: translateToString('metadata.types.product') },
		{ value: 'catalogue', label: translateToString('metadata.types.catalogue') },
		{ value: 'string', label: translateToString('metadata.types.string') },
		{ value: 'bool', label: translateToString('metadata.types.bool') },
		{ value: 'numeric', label: translateToString('metadata.types.numeric') },
	];
	const editOptions: DropdownData[] = [
		{
			label: translateToString('global.quick_action.edit'),
			value: 'EDIT'
		},
		{
			label: translateToString('global.quick_action.delete'),
			value: 'DELETE'
		},
	];

	function closeEditPopup() {
		setEditPopupOpen(false);
		setNewMetadata({ key: '', type: 'string' });
	}

	React.useEffect(() => {
		const lastGeneration = localStorage.getItem('last_metadata_generation');
		if (lastGeneration) {
			const lastGenDate = new Date(lastGeneration);
			const now = new Date();
			const diffMinutes = Math.floor((now.getTime() - lastGenDate.getTime()) / 60000);
			setDiffMinutes(30 - diffMinutes);
			if (diffMinutes < 30) {
				setIsAllowedToClickGenerate(false);
				setTimeout(() => setIsAllowedToClickGenerate(true), (30 - diffMinutes) * 60000);
			}
		}
		getTypedMetadata().then((res) => {
			setMetadata(res);
			setLoadingState(LoadingStateEnum.LOADED);
		}).catch(() => setLoadingState(LoadingStateEnum.ERROR));
	}, []);

	React.useEffect(() => {
		props.setToolBarState({
			title: translateToString('general.metadata'),
			bottomLeftToolbarComponent: <>
				{isAllowedToEditForms &&
					<div style={{ paddingLeft: '10px' }}>
						<RefreshButton isLoading={loadingRefresh} label={isAllowedToClickGenerate ? translateToString('metadata.generate') : `${translateToString('metadata.allow_generate')} ${diffMinutes} min`} disabled={!isAllowedToClickGenerate} onFilter={() => {
							setLoadingRefresh(true);
							localStorage.setItem('last_metadata_generation', new Date().toISOString());
							setDiffMinutes(30);
							setIsAllowedToClickGenerate(false);
							generateMetadata().then(() => {
								getTypedMetadata().then(setMetadata);
							}).finally(() => {
								setLoadingRefresh(false);
								localStorage.removeItem('last_metadata_generation');
								setIsAllowedToClickGenerate(true);
							});
						}} />
					</div>
				}
			</>,
			bottomRightToolbarComponent: <>
				{isAllowedToEditForms &&
					<Add size='22px' onClick={() => setEditPopupOpen(true)} />
				}
			</>
		});
	}, [isAllowedToEditForms, loadingRefresh, isAllowedToClickGenerate, diffMinutes]);

	const columns: Array<Column<TypedMetadata>> = React.useMemo(() => [
		{
			id: 'key',
			Header: translateToString('key'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
			>
				{row.key}
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		},
		{
			id: 'type',
			Header: 'Type',
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
			>
				{translateToString(`metadata.types.${row.type}`)}
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		},
		{
			id: 'created_at',
			Header: translateToString('created_at'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
			>
				{new Date(row.created_at).toLocaleDateString()}
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		},
		{
			id: 'updated_at',
			Header: translateToString('updated_at'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
			>
				{new Date(row.updated_at).toLocaleDateString()}
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		},
		{
			id: 'updated_by',
			Header: translateToString('updated_by'),
			accessor: row => <TableRow
				fontWeight="500"
				color={DarkGreySidely}
			>
				{users.find(u => u.id === row.updated_by)?.name ?? '-'}
			</TableRow>,
			width: undefined,
			minWidth: 150,
			type: 'string',
		}, {
			id: 'quick_actions',
			noHeaderEllipsis: true,
			Header: <img src={system_setting_icon} style={{ width: 18 }} />,
			accessor: row =>
				<Dropdown
					name='quick_action'
					datalist={editOptions}
					searchable={false}
					readOnly
					dropdownStyle={{ width: '10px', optionWidth: '100px', fontSize: 13, optionLeft: '-112px', containerTop: '-6px' }}
					JSXButton={() =>
						<ToolbarImageContainer rotate={'90deg'} marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} size={24} /></ToolbarImageContainer>
					}
					onChange={option => {
						if (option.value === 'EDIT') {
							setNewMetadata(row);
							setEditPopupOpen(true);
						}
						else if (option.value === 'DELETE') {
							alert({
								title: `${translateToString('metadata.delete_title')} ${row.key}`,
								content: translateToString('metadata.delete'),
								mode: 'delete'
							}).then((res) => {
								if (res.res === AlertRes.Ok) {
									deleteTypedMetadata(row.key).then(() => {
										getTypedMetadata().then(setMetadata);
									});
								}
							});
						}
					}}
				/>,
			width: 40,
			minWidth: 40,
			disableSortBy: true,
			disableFilter: true,
			unresizeable: true,
			freeze: 'right'
		}
	], [isAllowedToEditForms, metadata]);

	return <>
		<Popup
			isOpen={editPopupOpen}
			onClickOut={closeEditPopup}
			popupStyle={{ width: '500px', height: '350px', borderRadius: '8px' }}
			popupMode={PopupMode.Centered}
			hasBackground
		>
			<FlexDiv height='100%' flow='column' align='stretch' justify='space-between' fontSize='13px'>
				<DeletionHeader>
					<Translate id={newMetadata.created_at ? 'metadata.edit' : 'metadata.create'} />
					<img style={{ cursor: 'pointer' }} src={close} onClick={closeEditPopup} width={30} />
				</DeletionHeader>
				<DeletionContainer>
					<FlexDiv flow='column' gap='24px' align='stretch'>
						<FlexDiv gap='8px'>
							<Translate id='key' options={{ after: ' :' }} />
							<Input
								name='key'
								type='text'
								placeholder='Key'
								inputStyle={{ marginLeft: '5px', borderRadius: '5px' }}
								value={newMetadata.key ?? ''}
								onChange={value => setNewMetadata(m => ({ ...m, key: value as string }))}
								disabled={!!newMetadata.created_at}
							/>
						</FlexDiv>
						<FlexDiv gap='8px'>
							<Translate id='type' options={{ after: ' :' }} />
							<Dropdown
								name='type'
								datalist={dataList}
								dropdownStyle={{ width: '300px', optionWidth: '300px' }}
								searchable={false}
								selectedValue={dataList.find(option => option.value === (newMetadata.type ?? 'string'))}
								onChange={option => setNewMetadata(m => ({ ...m, type: option.value as MetadataType }))}
							/>
						</FlexDiv>
					</FlexDiv>
					<FlexDiv gap='16px' alignSelf='center'>
						<DefaultButton buttonStyle={ButtonStyle.White} onClick={closeEditPopup}><Translate id='cancel' /></DefaultButton>
						<DefaultButton
							disabled={!enableSave}
							onClick={() => {
								if (newMetadata.created_at) {
									if (!newMetadata.key || !newMetadata.type) return;
									putTypedMetadata({ key: newMetadata.key.trim(), type: newMetadata.type }).then(() => {
										getTypedMetadata().then(setMetadata);
										closeEditPopup();
									});
								}
								else {
									if (!newMetadata.key || !newMetadata.type) return;
									postTypedMetadata({ key: newMetadata.key.trim(), type: newMetadata.type })
										.then(() => {
											getTypedMetadata().then(setMetadata);
											closeEditPopup();
										})
										.catch((error) => {
											closeEditPopup();
											if (error?.response?.status === 409) {
												alert({
													title: translateToString('error'),
													content: <FlexDiv width='100%' justify='center'><Translate id='metadata.error.duplicate_key' /></FlexDiv>,
													noButtons: true,
													svg: 'warning'
												});
											} else {
												alert({
													title: translateToString('error'),
													content: <FlexDiv width='100%' justify='center'><Translate id='metadata.error.generic' /></FlexDiv>,
													noButtons: true,
													svg: 'warning'
												});
											}
										});
								}
							}}
						>
							<Translate id='save' />
						</DefaultButton>
					</FlexDiv>
				</DeletionContainer>
			</FlexDiv>
		</Popup>
		<Table
			height={`calc(100vh - ${TOOLBARS_HEIGHT + 50}px)`}
			columns={columns}
			data={metadata}
		/>
		<ComponentLoader allScreen loadingState={loadingState} />
	</>;
}
